import { useState, useEffect } from 'react'
import { Button } from './ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog'
import { Label } from './ui/label'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import toast from 'react-hot-toast'
import { useLazySyncClickupTimesheetQuery } from '@/redux/service/configuration'
import {
  Loader2,
  RefreshCw,
  Calendar as CalendarIcon,
  ArrowRight,
} from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import { Calendar } from './ui/calendar'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'
import { useGetProjectandEmployeeCoutQuery } from '@/redux/service/employee'

dayjs.extend(utc)

const SyncSchema = Yup.object().shape({
  startDate: Yup.date()
    .required('Start date is required')
    .max(new Date(), 'Start date cannot be in the future'),
  endDate: Yup.date()
    .required('End date is required')
    .max(new Date(), 'End date cannot be in the future'),
})

interface SyncFormValues {
  startDate: Date | undefined
  endDate: Date | undefined
}

export interface SyncProps {
  isMenuItem?: boolean
  isHidden?: boolean
  onItemClick?: () => void
}

const Sync = ({ isMenuItem, isHidden, onItemClick }: SyncProps) => {
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const handleTrigger = () => {
      setIsOpen(true)
    }
    window.addEventListener('trigger-sync-dialog', handleTrigger)
    return () =>
      window.removeEventListener('trigger-sync-dialog', handleTrigger)
  }, [])
  const [triggerSync, { isLoading }] = useLazySyncClickupTimesheetQuery()
  const { data: count, refetch } = useGetProjectandEmployeeCoutQuery(
    undefined,
    {
      pollingInterval: 60000,
    }
  )

  const [timeLeft, setTimeLeft] = useState<string | null>(null)

  useEffect(() => {
    const calculateTimeLeft = () => {
      const lastSyncedAt = count?.data?.sync?.last_synced_at
      const syncStartedAt = count?.data?.sync?.sync_started_at

      if (syncStartedAt) {
        setTimeLeft(null)
        return
      }

      if (lastSyncedAt) {
        const lastSynced = dayjs.utc(lastSyncedAt).local()
        const nextSync = lastSynced.add(60, 'minute')
        const now = dayjs()
        const diff = nextSync.diff(now, 'second')

        if (diff > 0) {
          const minutes = Math.floor(diff / 60)
          const seconds = diff % 60
          setTimeLeft(`${minutes}:${seconds.toString().padStart(2, '0')}`)
        } else {
          setTimeLeft(null)
        }
      } else {
        setTimeLeft(null)
      }
    }

    calculateTimeLeft()
    const timer = setInterval(calculateTimeLeft, 1000)

    return () => clearInterval(timer)
  }, [count?.data?.sync?.last_synced_at, count?.data?.sync?.sync_started_at])

  const initialValues: SyncFormValues = {
    startDate: undefined,
    endDate: undefined,
  }

  const handleSubmit = async (values: SyncFormValues) => {
    if (!values.startDate || !values.endDate) return

    try {
      // Start date at 1:00 AM (1 hour into the day)
      const startMs = dayjs(values.startDate)
        .startOf('day')
        .add(1, 'hour')
        .valueOf()
      // End date at end of day (23:59:59.999)
      const endMs = dayjs(values.endDate).endOf('day').valueOf()

      const response = await triggerSync({
        startDate: startMs,
        endDate: endMs,
      }).unwrap()

      toast.success(response?.message || 'Timesheet synced successfully!')
      refetch()
      setIsOpen(false)
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ||
        'Failed to sync timesheet'

      toast.error(message)
    }
  }
  if (isHidden) {
    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className='sm:max-w-[480px] p-0 gap-0 overflow-hidden rounded-2xl border-border/40 shadow-2xl'>
          <DialogHeader className='px-6 py-5 border-b border-border bg-muted/20'>
            <div className='flex items-center gap-3.5'>
              <div className='flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 shrink-0 shadow-sm'>
                <RefreshCw className='w-5 h-5 text-primary' />
              </div>
              <div className='space-y-0.5'>
                <DialogTitle className='text-lg font-bold tracking-tight'>
                  Sync ClickUp
                </DialogTitle>
                <DialogDescription className='text-xs text-muted-foreground font-medium'>
                  Synchronize your workspace timesheets for the selected period.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <Formik
            initialValues={initialValues}
            validationSchema={SyncSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, setFieldValue }) => (
              <Form className='p-6'>
                <div className='grid grid-cols-2 gap-4 mb-8'>
                  {/* START DATE */}
                  <div className='space-y-2'>
                    <Label className='text-[11px] font-bold text-muted-foreground uppercase tracking-widest ml-0.5'>
                      Start Date
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant='outline'
                          className={cn(
                            'w-full justify-start text-left font-normal h-10 rounded-lg border-input bg-background hover:bg-accent/40 transition-all duration-200 shadow-sm',
                            !values.startDate && 'text-muted-foreground',
                            errors.startDate &&
                              touched.startDate &&
                              'border-destructive/50 bg-destructive/5'
                          )}
                        >
                          <CalendarIcon className='mr-2.5 h-4 w-4 opacity-60' />
                          <span className='text-sm font-medium'>
                            {values.startDate
                              ? format(values.startDate, 'MMM dd, yyyy')
                              : 'Select date'}
                          </span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className='p-0 w-auto rounded-xl shadow-xl border-border overflow-hidden'
                        align='start'
                      >
                        <Calendar
                          mode='single'
                          selected={values.startDate}
                          onSelect={date => setFieldValue('startDate', date)}
                          initialFocus
                          startMonth={new Date(2000, 0)}
                          endMonth={new Date()}
                          disabled={{ after: new Date() }}
                          captionLayout='dropdown'
                        />
                      </PopoverContent>
                    </Popover>
                    {errors.startDate && touched.startDate && (
                      <p className='text-[11px] font-semibold text-destructive mt-1.5 flex items-center gap-1.5 ml-0.5'>
                        <span className='w-1 h-1 rounded-full bg-destructive' />
                        {errors.startDate as string}
                      </p>
                    )}
                  </div>

                  {/* END DATE */}
                  <div className='space-y-2'>
                    <Label className='text-[11px] font-bold text-muted-foreground uppercase tracking-widest ml-0.5'>
                      End Date
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant='outline'
                          className={cn(
                            'w-full justify-start text-left font-normal h-10 rounded-lg border-input bg-background hover:bg-accent/40 transition-all duration-200 shadow-sm',
                            !values.endDate && 'text-muted-foreground',
                            errors.endDate &&
                              touched.endDate &&
                              'border-destructive/50 bg-destructive/5'
                          )}
                        >
                          <CalendarIcon className='mr-2.5 h-4 w-4 opacity-60' />
                          <span className='text-sm font-medium'>
                            {values.endDate
                              ? format(values.endDate, 'MMM dd, yyyy')
                              : 'Select date'}
                          </span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className='p-0 w-auto rounded-xl shadow-xl border-border overflow-hidden'
                        align='start'
                      >
                        <Calendar
                          mode='single'
                          selected={values.endDate}
                          onSelect={date => setFieldValue('endDate', date)}
                          numberOfMonths={1}
                          startMonth={new Date(2000, 0)}
                          endMonth={new Date()}
                          disabled={{ after: new Date() }}
                          captionLayout='dropdown'
                        />
                      </PopoverContent>
                    </Popover>
                    {errors.endDate && touched.endDate && (
                      <p className='text-[11px] font-semibold text-destructive mt-1.5 flex items-center gap-1.5 ml-0.5'>
                        <span className='w-1 h-1 rounded-full bg-destructive' />
                        {errors.endDate as string}
                      </p>
                    )}
                  </div>
                </div>

                <div className='flex items-center justify-end gap-3 pt-5 border-t border-border/60'>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    onClick={() => setIsOpen(false)}
                    className='h-9 px-5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors'
                  >
                    Cancel
                  </Button>
                  <Button
                    type='submit'
                    size='sm'
                    disabled={isLoading}
                    className='h-9 px-6 text-xs font-bold shadow-lg shadow-primary/20 flex items-center gap-2 group min-w-[120px]'
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className='w-3.5 h-3.5 animate-spin' />
                        <span>Syncing...</span>
                      </>
                    ) : (
                      <>
                        <span>Sync Now</span>
                        <ArrowRight className='w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform' />
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <div className={cn(!isMenuItem && 'flex items-center justify-end gap-4')}>
      {isMenuItem ? (
        <button
          onClick={e => {
            e.stopPropagation()
            window.dispatchEvent(new CustomEvent('trigger-sync-dialog'))
            onItemClick?.()
          }}
          disabled={
            !!count?.data?.sync?.sync_started_at || !!timeLeft || isLoading
          }
          className='flex items-center justify-start gap-3 w-full px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-accent rounded-xl transition-colors group disabled:opacity-50 disabled:cursor-not-allowed'
        >
          <div className='p-2 bg-muted rounded-lg group-hover:bg-accent transition-colors flex-shrink-0'>
            <RefreshCw
              className={cn(
                'w-4 h-4 text-muted-foreground transition-transform duration-500',
                (isLoading || !!count?.data?.sync?.sync_started_at) &&
                  'animate-spin'
              )}
            />
          </div>
          <span className='truncate text-foreground'>
            {count?.data?.sync?.sync_started_at
              ? 'Syncing...'
              : timeLeft
                ? `Next sync in ${timeLeft}`
                : 'Sync Now'}
          </span>
        </button>
      ) : (
        <Button
          onClick={e => {
            e.stopPropagation()
            setIsOpen(true)
          }}
          disabled={
            !!count?.data?.sync?.sync_started_at || !!timeLeft || isLoading
          }
          className='gap-2.5 bg-transparent rounded-full text-foreground hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center'
        >
          <div className='flex gap-1'>
            <RefreshCw
              className={cn(
                'w-5 h-5 transition-transform duration-500',
                (isLoading || !!count?.data?.sync?.sync_started_at) &&
                  'animate-spin'
              )}
            />
            {count?.data?.sync?.sync_started_at || timeLeft ? (
              <span className='whitespace-nowrap'>
                {count?.data?.sync?.sync_started_at
                  ? 'Syncing...'
                  : timeLeft
                    ? `Next sync in ${timeLeft}`
                    : null}
              </span>
            ) : null}
          </div>
        </Button>
      )}

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className='sm:max-w-[480px] p-0 gap-0 overflow-hidden rounded-2xl border-border/40 shadow-2xl'>
          <DialogHeader className='px-6 py-5 border-b border-border bg-muted/20'>
            <div className='flex items-center gap-3.5'>
              <div className='flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 shrink-0 shadow-sm'>
                <RefreshCw className='w-5 h-5 text-primary' />
              </div>
              <div className='space-y-0.5'>
                <DialogTitle className='text-lg font-bold tracking-tight'>
                  Sync ClickUp
                </DialogTitle>
                <DialogDescription className='text-xs text-muted-foreground font-medium'>
                  Synchronize your workspace timesheets for the selected period.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <Formik
            initialValues={initialValues}
            validationSchema={SyncSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, setFieldValue }) => (
              <Form className='p-6'>
                <div className='grid grid-cols-2 gap-4 mb-8'>
                  {/* START DATE */}
                  <div className='space-y-2'>
                    <Label className='text-[11px] font-bold text-muted-foreground uppercase tracking-widest ml-0.5'>
                      Start Date
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant='outline'
                          className={cn(
                            'w-full justify-start text-left font-normal h-10 rounded-lg border-input bg-background hover:bg-accent/40 transition-all duration-200 shadow-sm',
                            !values.startDate && 'text-muted-foreground',
                            errors.startDate &&
                              touched.startDate &&
                              'border-destructive/50 bg-destructive/5'
                          )}
                        >
                          <CalendarIcon className='mr-2.5 h-4 w-4 opacity-60' />
                          <span className='text-sm font-medium'>
                            {values.startDate
                              ? format(values.startDate, 'MMM dd, yyyy')
                              : 'Select date'}
                          </span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className='p-0 w-auto rounded-xl shadow-xl border-border overflow-hidden'
                        align='start'
                      >
                        <Calendar
                          mode='single'
                          selected={values.startDate}
                          onSelect={date => setFieldValue('startDate', date)}
                          initialFocus
                          startMonth={new Date(2000, 0)}
                          endMonth={new Date()}
                          disabled={{ after: new Date() }}
                          captionLayout='dropdown'
                        />
                      </PopoverContent>
                    </Popover>
                    {errors.startDate && touched.startDate && (
                      <p className='text-[11px] font-semibold text-destructive mt-1.5 flex items-center gap-1.5 ml-0.5'>
                        <span className='w-1 h-1 rounded-full bg-destructive' />
                        {errors.startDate as string}
                      </p>
                    )}
                  </div>

                  {/* END DATE */}
                  <div className='space-y-2'>
                    <Label className='text-[11px] font-bold text-muted-foreground uppercase tracking-widest ml-0.5'>
                      End Date
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant='outline'
                          className={cn(
                            'w-full justify-start text-left font-normal h-10 rounded-lg border-input bg-background hover:bg-accent/40 transition-all duration-200 shadow-sm',
                            !values.endDate && 'text-muted-foreground',
                            errors.endDate &&
                              touched.endDate &&
                              'border-destructive/50 bg-destructive/5'
                          )}
                        >
                          <CalendarIcon className='mr-2.5 h-4 w-4 opacity-60' />
                          <span className='text-sm font-medium'>
                            {values.endDate
                              ? format(values.endDate, 'MMM dd, yyyy')
                              : 'Select date'}
                          </span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className='p-0 w-auto rounded-xl shadow-xl border-border overflow-hidden'
                        align='start'
                      >
                        <Calendar
                          mode='single'
                          selected={values.endDate}
                          onSelect={date => setFieldValue('endDate', date)}
                          numberOfMonths={1}
                          startMonth={new Date(2000, 0)}
                          endMonth={new Date()}
                          disabled={{ after: new Date() }}
                          captionLayout='dropdown'
                        />
                      </PopoverContent>
                    </Popover>
                    {errors.endDate && touched.endDate && (
                      <p className='text-[11px] font-semibold text-destructive mt-1.5 flex items-center gap-1.5 ml-0.5'>
                        <span className='w-1 h-1 rounded-full bg-destructive' />
                        {errors.endDate as string}
                      </p>
                    )}
                  </div>
                </div>

                <div className='flex items-center justify-end gap-3 pt-5 border-t border-border/60'>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    onClick={() => setIsOpen(false)}
                    className='h-9 px-5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors'
                  >
                    Cancel
                  </Button>
                  <Button
                    type='submit'
                    size='sm'
                    disabled={isLoading}
                    className='h-9 px-6 text-xs font-bold shadow-lg shadow-primary/20 flex items-center gap-2 group min-w-[120px]'
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className='w-3.5 h-3.5 animate-spin' />
                        <span>Syncing...</span>
                      </>
                    ) : (
                      <>
                        <span>Sync Now</span>
                        <ArrowRight className='w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform' />
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default Sync
